import { Flask, Plus, Trash } from '@phosphor-icons/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'src/components/Button';
import {
  CallSelectorState,
  CallSelectorViewModal,
} from 'src/components/CallSelector';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { Modal } from 'src/components/Modal';
import { TestResultPanel } from 'src/components/PostCallDataExtractionModal/TestResultPanel';
import { SelectBox } from 'src/components/SelectBox';
import { Textarea, TextareaSubTextPosition } from 'src/components/Textarea';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import {
  Agent,
  PostCallAnalysisItem,
  PostCallAnalysisItemType,
  PostCallAnalysisResultItem,
} from 'src/interfaces/agent.interface';
import {
  Call,
  getAgentId,
  PreviewPostCallAnalysisResult,
} from 'src/interfaces/call.interface';
import { cn } from 'src/utils/className';

const POST_CALL_ANALYSIS_ITEM_ID_PATTERN = /^[a-z0-9-]*$/;

type ModalView = 'edit' | 'selectCall';

type PostCallDataExtractionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  agent: Agent;
  item: PostCallAnalysisItem | null;
  existingItems: PostCallAnalysisItem[];
  onSave: (item: PostCallAnalysisItem) => void;
  onDelete?: (id: string) => void;
  disabled?: boolean;
};

const typeOptions = [
  { value: PostCallAnalysisItemType.BOOLEAN, label: 'Yes / No' },
  { value: PostCallAnalysisItemType.NUMBER, label: 'Number' },
  { value: PostCallAnalysisItemType.ENUM, label: 'Selector' },
  { value: PostCallAnalysisItemType.STRING, label: 'Text' },
];

const sanitizeId = (raw: string) =>
  raw
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .slice(0, 80);

const generateIdFromLabel = (label: string) => sanitizeId(label);

// Cache for agent names
const agentNameCache: Record<string, string> = {};

export const PostCallDataExtractionModal: React.FC<
  PostCallDataExtractionModalProps
> = ({
  isOpen,
  onClose,
  agent,
  item,
  existingItems,
  onSave,
  onDelete,
  disabled = false,
}) => {
  const notification = useNotification();
  const { get, post } = useAuthedRequest();

  const isEditing = !!item;

  // Form state
  const [label, setLabel] = useState('');
  const [id, setId] = useState('');
  const [instruction, setInstruction] = useState('');
  const [type, setType] = useState<PostCallAnalysisItemType | null>(null);
  const [enumOptions, setEnumOptions] = useState<string[]>([]);

  // View state
  const [view, setView] = useState<ModalView>('edit');

  // Test state
  const [testCall, setTestCall] = useState<Call | null>(null);
  const [testAgentName, setTestAgentName] = useState<string>('');
  const [testResult, setTestResult] =
    useState<PostCallAnalysisResultItem | null>(null);
  const [isEvaluating, setIsEvaluating] = useState(false);

  // Call selector state (preserved when switching views)
  const [callSelectorState, setCallSelectorState] =
    useState<CallSelectorState | null>(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) return;

    if (item) {
      setLabel(item.label || '');
      setId(item.id);
      setInstruction(item.instructions || '');
      setType(item.type);
      setEnumOptions(item.enumOptions || []);
    } else {
      setLabel('');
      setId('');
      setInstruction('');
      setType(null);
      setEnumOptions([]);
    }

    // Reset test state when modal opens
    setView('edit');
    setTestCall(null);
    setTestAgentName('');
    setTestResult(null);
    setIsEvaluating(false);
    setCallSelectorState(null);
  }, [isOpen, item]);

  const instructionPlaceholder = useMemo(() => {
    switch (type) {
      case PostCallAnalysisItemType.BOOLEAN:
        return 'e.g., The customer consented to be contacted via SMS.';
      case PostCallAnalysisItemType.NUMBER:
        return 'e.g., The number of issues the customer reported.';
      case PostCallAnalysisItemType.ENUM:
        return 'e.g., Which plan was the customer interested in?';
      case PostCallAnalysisItemType.STRING:
        return 'e.g., Give a 2-3 sentence summary of the call.';
      default:
        return 'Select a type to see an example';
    }
  }, [type]);

  const trimmedLabel = useMemo(() => label.trim(), [label]);
  const trimmedInstruction = useMemo(() => instruction.trim(), [instruction]);
  const trimmedId = useMemo(() => id.trim(), [id]);

  const isEnumType = type === PostCallAnalysisItemType.ENUM;
  const normalizedEnumOptions = useMemo(
    () => enumOptions.map((o) => o.trim()),
    [enumOptions],
  );

  const isDuplicateId = useMemo(() => {
    if (!trimmedId) return false;
    return existingItems.some(
      (i) => i.id === trimmedId && (!item || i.id !== item.id),
    );
  }, [existingItems, item, trimmedId]);

  const isFormValid = useMemo(() => {
    if (!trimmedLabel) return false;
    if (!trimmedInstruction) return false;
    if (!trimmedId) return false;
    if (!type) return false;

    if (isEnumType) {
      if (normalizedEnumOptions.length === 0) return false;
      if (normalizedEnumOptions.some((o) => !o)) return false;
    }

    return true;
  }, [
    isEnumType,
    normalizedEnumOptions,
    trimmedId,
    trimmedInstruction,
    trimmedLabel,
    type,
  ]);

  const canSubmit = !disabled && isFormValid;

  const canAutoGenerateId = !isEditing;

  const handleLabelChange = (nextLabel: string) => {
    setLabel(nextLabel);
    if (!canAutoGenerateId) return;
    setId(generateIdFromLabel(nextLabel));
  };

  const handleIdChange = (raw: string) => {
    const next = sanitizeId(raw);
    setId(next);
  };

  const handleAddChoice = () => {
    setEnumOptions((prev) => [...prev, '']);
  };

  const handleChangeChoiceAt = (idx: number, value: string) => {
    setEnumOptions((prev) => prev.map((v, i) => (i === idx ? value : v)));
  };

  const handleDeleteChoiceAt = (idx: number) => {
    setEnumOptions((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    if (!trimmedLabel) {
      notification.error('Description is required.');
      return;
    }

    if (!trimmedInstruction) {
      notification.error(
        'Instructions for determining this insight are required.',
      );
      return;
    }

    if (!trimmedId) {
      notification.error('ID is required.');
      return;
    }
    if (!POST_CALL_ANALYSIS_ITEM_ID_PATTERN.test(trimmedId)) {
      notification.error(
        'ID can only contain lowercase letters, numbers, and dashes.',
      );
      return;
    }

    if (!type) {
      notification.error('Type is required.');
      return;
    }

    if (type === PostCallAnalysisItemType.ENUM) {
      const normalized = normalizedEnumOptions.filter(Boolean);
      if (normalized.length === 0) {
        notification.error('At least one choice is required.');
        return;
      }
      if (normalized.length !== normalizedEnumOptions.length) {
        notification.error('Choices cannot be empty.');
        return;
      }
    }

    if (isDuplicateId) {
      notification.error('Another call insight already uses this ID.');
      return;
    }

    const saved: PostCallAnalysisItem = {
      id: trimmedId,
      label: trimmedLabel,
      instructions: trimmedInstruction,
      type,
      ...(type === PostCallAnalysisItemType.ENUM && {
        enumOptions: normalizedEnumOptions,
      }),
    };

    onSave(saved);
  };

  const handleDelete = () => {
    if (!item || !onDelete) return;
    onDelete(item.id);
  };

  // Test functionality
  const handleTestClick = () => {
    setView('selectCall');
  };

  const handleStopTesting = () => {
    setTestCall(null);
    setTestAgentName('');
    setTestResult(null);
    setIsEvaluating(false);
    setCallSelectorState(null);
  };

  const runEvaluation = useCallback(
    async (call: Call) => {
      if (!canSubmit) {
        return;
      }

      setIsEvaluating(true);
      setTestResult(null);

      try {
        const insightItem: PostCallAnalysisItem = {
          id: trimmedId,
          label: trimmedLabel,
          instructions: trimmedInstruction,
          type: type!,
          ...(type === PostCallAnalysisItemType.ENUM && {
            enumOptions: normalizedEnumOptions,
          }),
        };

        const response: PreviewPostCallAnalysisResult = await post(
          `/calls/${call.id}/post_call_analysis/preview`,
          { items: [insightItem] },
        );

        const result = response.contentJson[trimmedId] || null;
        if (!result) {
          notification.error(
            'No result came back for this test. Please try again.',
          );
          return;
        }
        setTestResult(result);
      } catch (error) {
        console.error('Error evaluating insight:', error);
        notification.error('Failed to evaluate insight. Please try again.');
      } finally {
        setIsEvaluating(false);
      }
    },
    [
      type,
      canSubmit,
      trimmedId,
      trimmedLabel,
      trimmedInstruction,
      normalizedEnumOptions,
      post,
      notification,
    ],
  );

  const handleSelectCall = useCallback(
    async (call: Call, selectorState: CallSelectorState) => {
      setTestCall(call);
      // Store the selector state for when user clicks "Change"
      setCallSelectorState(selectorState);
      // Reset previous result so evaluation runs again
      setTestResult(null);

      // Fetch agent name
      const agentId = getAgentId(call.agent);
      if (agentId) {
        if (agentNameCache[agentId]) {
          setTestAgentName(agentNameCache[agentId]);
        } else {
          try {
            const agent = await get(`/agents/${agentId}`);
            agentNameCache[agentId] = agent.name;
            setTestAgentName(agent.name);
          } catch {
            agentNameCache[agentId] = '(deleted)';
            setTestAgentName('(deleted)');
          }
        }
      } else {
        setTestAgentName('(deleted)');
      }

      setView('edit');
      // Run evaluation after selecting call
      runEvaluation(call);
    },
    [get, runEvaluation],
  );

  const handleCancelSelection = () => {
    setView('edit');
  };

  const handleChangeTestCall = () => {
    setView('selectCall');
  };

  const handleRetest = () => {
    if (testCall) {
      void runEvaluation(testCall);
    }
  };

  const title = isEditing ? 'Edit Call Insight' : 'Add Call Insight';
  const isTesting = testCall !== null;
  const insightModalMaxWidthClass = isTesting ? 'lg:max-w-6xl' : 'lg:max-w-3xl';

  // Render call selector view
  if (view === 'selectCall') {
    return (
      <CallSelectorViewModal
        isOpen={isOpen}
        onSelectCall={handleSelectCall}
        onCancel={handleCancelSelection}
        onClose={onClose}
        initialState={callSelectorState || undefined}
        defaultAgent={agent.id ? agent : undefined}
      />
    );
  }

  const renderFooter = () => (
    <div className="flex justify-between gap-4 pt-4 px-6 pb-6 bg-neutral-0 flex-shrink-0 border-t border-neutral-200">
      <div>
        {isEditing && onDelete && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleDelete}
            disabled={disabled}
            size="small"
          >
            Delete
          </Button>
        )}
      </div>
      <div className="flex gap-2">
        {isTesting ? (
          <Button variant="outlined" onClick={handleStopTesting} size="small">
            Close Test Panel
          </Button>
        ) : (
          <Button
            variant="outlined"
            onClick={handleTestClick}
            disabled={!canSubmit}
            size="small"
          >
            <Flask className="w-4 h-4 mr-2" weight="fill" />
            Test Insight
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={onClose}
          size="small"
          disabled={disabled}
        >
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={!canSubmit} size="small">
          {isEditing ? 'Apply Changes' : 'Add Call Insight'}
        </Button>
      </div>
    </div>
  );

  // Render edit view
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle="Specify what you want to learn from each call."
      footer={renderFooter()}
      showModalBorder
      showClose
      className={cn('transition-all duration-300', insightModalMaxWidthClass)}
    >
      {/* Flex container for main content and test panel */}
      <div className="flex -mx-6">
        {/* Main form */}
        <div
          className={cn(
            'flex flex-col flex-1 p-6',
            isTesting && 'border-r border-neutral-200',
          )}
        >
          <div className="space-y-6">
            <Input
              label="Description"
              id="post-call-analysis-item-label"
              placeholder="e.g., Customer consent to SMS"
              value={label}
              onChange={(e) => handleLabelChange(e.target.value)}
              disabled={disabled}
              required
            />

            <Input
              label="ID"
              id="post-call-analysis-item-id"
              placeholder="e.g., customer-consent"
              value={id}
              onChange={(e) => handleIdChange(e.target.value)}
              disabled={disabled}
              required
            />

            <div>
              <Label required>Type</Label>
              <SelectBox
                value={
                  type
                    ? typeOptions.find((o) => o.value === type) || null
                    : null
                }
                options={typeOptions}
                onChange={(val) => setType(val as PostCallAnalysisItemType)}
                disabled={disabled}
              />
            </div>

            <Textarea
              label="How to determine this insight"
              subText="Write brief instructions for determining the value from the call."
              subTextPosition={TextareaSubTextPosition.ABOVE}
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              rows={6}
              className="resize-none"
              disabled={disabled}
              placeholder={instructionPlaceholder}
              required
            />

            {type === PostCallAnalysisItemType.ENUM && (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-body font-medium text-primary-body">
                      Choices
                    </div>
                    <p className="text-description text-neutral-400 mt-2">
                      Add the allowed choices for this selector.
                    </p>
                  </div>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAddChoice}
                    disabled={disabled}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Choice
                  </Button>
                </div>

                <div className="space-y-2">
                  {enumOptions.map((opt, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input
                        id={`post-call-analysis-item-choice-${idx}`}
                        placeholder="e.g., yes"
                        value={opt}
                        onChange={(e) =>
                          handleChangeChoiceAt(idx, e.target.value)
                        }
                        disabled={disabled}
                        className="flex-1"
                        required
                      />
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => handleDeleteChoiceAt(idx)}
                        disabled={disabled}
                        className="h-10 px-3 flex items-center justify-center"
                      >
                        <Trash className="w-5 h-5" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Test Result Panel */}
        {isTesting && (
          <TestResultPanel
            testCall={testCall!}
            agentName={testAgentName}
            result={testResult}
            isEvaluating={isEvaluating}
            canRetest={canSubmit}
            onChangeTestCall={handleChangeTestCall}
            onRetest={handleRetest}
            notification={notification}
          />
        )}
      </div>
    </Modal>
  );
};
